import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Page, useToast } from "@theg1group/ui";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ReactNode } from "react";
import { api } from "../lib/api.ts";
import { formatAlertDetail } from "../lib/alertFormat.ts";

interface RepRow {
	source: string;
	status: string;
	response: string | null;
	checked_at: string;
}
interface DomainDetail {
	domain: Record<string, unknown>;
	records: Array<{ name: string; type: string; value: string; priority: number | null; resolved_at: string }>;
	linked: Array<{
		rel: string;
		dst_type: string;
		label: string;
		asn: number | null;
		asn_org: string | null;
		country: string | null;
		ptr: string | null;
	}>;
	selectors: Array<{ selector: string; algo: string | null }>;
	reputation: RepRow[];
	watched: { active: boolean; note: string | null; last_checked_at: string | null } | null;
	certs: Array<{ common_name: string | null; issuer: string | null; not_before: string | null; not_after: string | null }>;
}
interface TimelineEvent {
	ts: string;
	kind: string;
	severity?: string;
	label: string;
	source?: string | null;
	detail?: unknown;
}
interface ConnSignal {
	via: string;
	value: string;
	df: number;
	idf: number;
	score: number;
	class: "strong" | "probabilistic";
}
interface DomainConn {
	domain: string;
	score: number;
	signals: ConnSignal[];
}

function tlVariant(e: TimelineEvent): "success" | "warning" | "error" | "info" | "default" {
	if (e.severity === "critical") return "error";
	if (e.severity === "warn") return "warning";
	if (e.kind === "cert") return "default";
	return "info";
}

function repVariant(status: string): "success" | "warning" | "error" | "default" {
	if (status === "blacklisted") return "error";
	if (status === "greylisted") return "warning";
	if (status === "clean") return "success";
	return "default"; // blocked | error
}

export function DomainView() {
	const { name = "" } = useParams();
	const qc = useQueryClient();
	const toast = useToast();
	const [includeCommon, setIncludeCommon] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["domain", name],
		queryFn: () => api.get<DomainDetail>(`/api/domains/${encodeURIComponent(name)}`),
		enabled: !!name,
	});
	const timeline = useQuery({
		queryKey: ["domain-timeline", name],
		queryFn: () => api.get<{ events: TimelineEvent[] }>(`/api/domains/${encodeURIComponent(name)}/timeline`),
		enabled: !!name,
	});
	const connections = useQuery({
		queryKey: ["domain-connections", name, includeCommon],
		queryFn: () =>
			api.get<{ connections: DomainConn[] }>(
				`/api/domains/${encodeURIComponent(name)}/connections${includeCommon ? "?includeCommon=1" : ""}`,
			),
		enabled: !!name,
	});
	const invalidate = () => {
		qc.invalidateQueries({ queryKey: ["domain", name] });
		qc.invalidateQueries({ queryKey: ["domain-connections", name] });
	};
	const resolve = useMutation({
		mutationFn: () => api.post(`/api/domains/${encodeURIComponent(name)}/resolve`),
		onSuccess: () => {
			toast.addToast("success", "Resolved");
			invalidate();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const watch = useMutation({
		mutationFn: () =>
			data?.watched
				? api.del(`/api/monitor/watch/${encodeURIComponent(name)}`)
				: api.post(`/api/monitor/watch`, { domain: name }),
		onSuccess: () => {
			toast.addToast("success", data?.watched ? "Unwatched" : "Watching");
			invalidate();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});
	const checkRep = useMutation({
		mutationFn: () => api.post(`/api/monitor/reputation/${encodeURIComponent(name)}/check`),
		onSuccess: () => {
			toast.addToast("success", "Reputation checked");
			invalidate();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const byType = new Map<string, DomainDetail["records"]>();
	for (const r of data?.records ?? []) {
		const arr = byType.get(r.type) ?? [];
		arr.push(r);
		byType.set(r.type, arr);
	}
	const ips = data?.linked.filter((l) => l.dst_type === "ip") ?? [];
	const ns = data?.linked.filter((l) => l.dst_type === "nameserver") ?? [];
	const mx = data?.linked.filter((l) => l.dst_type === "host" && l.rel === "HAS_MX") ?? [];
	const d = data?.domain;
	const conns = connections.data?.connections ?? [];

	return (
		<Page
			title={name}
			description={d ? `registrar: ${(d.rdap_registrar as string) ?? "—"}` : undefined}
			actions={
				<div className="flex gap-2 items-center">
					<Link to={`/graph?focus=${encodeURIComponent(name)}`} className="text-primary text-sm hover:underline">
						View in graph →
					</Link>
					<Button variant="outline" loading={watch.isPending} onClick={() => watch.mutate()}>
						{data?.watched ? "Unwatch" : "Watch"}
					</Button>
					<Button variant="secondary" loading={resolve.isPending} onClick={() => resolve.mutate()}>
						Resolve now
					</Button>
				</div>
			}
		>
			{isLoading && <Panel>Loading…</Panel>}
			{!isLoading && !data && <Panel>Domain not seen yet.</Panel>}
			{d && (
				<Panel className="mb-3.5">
					<div className="grid gap-2.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
						<KV k="TLD" v={d.tld as string} />
						<KV k="Registered" v={fmt(d.rdap_created as string)} />
						<KV k="Expires" v={fmt(d.rdap_expires as string)} />
						<KV k="First seen" v={fmt(d.first_seen as string)} />
						<KV k="Last seen" v={fmt(d.last_seen as string)} />
						{d.wildcard ? <KV k="Wildcard zone" v="yes (A-pivot unreliable)" /> : null}
						{d.rdap_status ? <KV k="Status" v={d.rdap_status as string} /> : null}
					</div>
				</Panel>
			)}

			{/* Timeline */}
			<Panel title={`Timeline (${timeline.data?.events.length ?? 0})`} className="mb-3.5">
				{timeline.data?.events.length ? (
					<div className="flex flex-col gap-1.5">
						{timeline.data.events.map((e, i) => (
							<div key={`${e.ts}:${e.kind}:${i}`} className="flex items-baseline gap-2 text-sm">
								<span className="text-muted text-[11px] font-mono shrink-0 w-32">
									{e.kind === "cert" || e.kind === "registered" ? fmt(e.ts) : fmtDateTime(e.ts)}
								</span>
								<Badge variant={tlVariant(e)}>{e.kind}</Badge>
								<span className="text-content">{e.label}</span>
								{formatAlertDetail(e.kind === "alert" ? e.label : e.kind, e.detail) ? (
									<span className="text-muted text-xs font-mono break-all">
										{formatAlertDetail(e.kind === "alert" ? e.label : e.kind, e.detail)}
									</span>
								) : null}
							</div>
						))}
					</div>
				) : (
					<div className="text-xs text-muted">No events yet.</div>
				)}
			</Panel>

			{/* Reputation */}
			<Panel
				title="Reputation (blacklists)"
				className="mb-3.5"
				action={
					<Button variant="outline" size="sm" loading={checkRep.isPending} onClick={() => checkRep.mutate()}>
						Check now
					</Button>
				}
			>
				{data?.reputation.length ? (
					<div className="flex flex-wrap gap-2">
						{data.reputation.map((r) => (
							<span key={r.source} className="text-sm flex items-center gap-1">
								<Badge variant={repVariant(r.status)}>{r.source}: {r.status}</Badge>
								{r.response ? <span className="text-muted font-mono text-[11px]">{r.response}</span> : null}
							</span>
						))}
					</div>
				) : (
					<div className="text-xs text-muted">
						Not checked yet. <em>Check now</em> runs DNSBL/URIBL lookups (Spamhaus DQS key gives reliable
						verdicts; without it public zones may report <code>blocked</code>).
					</div>
				)}
			</Panel>

			{/* Connected domains (scored) */}
			<Panel
				title={`Connected domains (${conns.length})`}
				className="mb-3.5"
				action={
					<label className="text-xs text-muted flex items-center gap-1.5 cursor-pointer">
						<input type="checkbox" checked={includeCommon} onChange={(e) => setIncludeCommon(e.target.checked)} />
						show common signals
					</label>
				}
			>
				{conns.length ? (
					<div className="flex flex-col gap-2.5">
						{conns.map((cn) => (
							<div key={cn.domain} className="text-sm">
								<div className="flex items-center gap-2">
									<Badge variant="info">score {cn.score}</Badge>
									<Link to={`/domains/${encodeURIComponent(cn.domain)}`} className="text-primary hover:underline font-medium">
										{cn.domain}
									</Link>
								</div>
								<div className="flex flex-col gap-0.5 mt-1 pl-2 border-l border-line">
									{cn.signals.map((s, i) => (
										<div key={`${s.via}:${s.value}:${i}`} className="flex items-center gap-2 text-xs">
											<Badge variant={s.class === "strong" ? "success" : "default"} size="sm">
												{s.via}
											</Badge>
											<span className="text-muted font-mono break-all">{s.value}</span>
											<span className="text-muted">idf {s.idf} · df {s.df}</span>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				) : (
					<Empty />
				)}
			</Panel>

			<div className="grid gap-3.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
				<Panel title="Nameservers">
					{ns.length ? ns.map((n) => <div key={n.label} className="font-mono text-xs text-content">{n.label}</div>) : <Empty />}
				</Panel>
				<Panel title="Resolves to (IP)">
					{ips.length ? (
						ips.map((i) => (
							<div key={i.label} className="font-mono text-xs text-content mb-0.5">
								{i.label} {i.asn ? <Badge variant="info">AS{i.asn} {i.country}</Badge> : null}
								{i.ptr ? <span className="text-muted"> · {i.ptr}</span> : null}
							</div>
						))
					) : (
						<Empty />
					)}
				</Panel>
				<Panel title="MX">
					{mx.length ? mx.map((x) => <div key={x.label} className="font-mono text-xs text-content">{x.label}</div>) : <Empty />}
				</Panel>
				<Panel title="DKIM selectors">
					{data?.selectors.length ? (
						data.selectors.map((s) => <div key={s.selector} className="font-mono text-xs text-content">{s.selector}</div>)
					) : (
						<Empty />
					)}
				</Panel>
			</div>

			<Panel title="TXT / SPF / DMARC" className="mt-3.5">
				{(byType.get("TXT") ?? []).map((r) => (
					<div key={`${r.name}:${r.value}`} className="font-mono text-xs text-content mb-1 break-all">
						<span className="text-muted">{r.name}</span> {r.value}
					</div>
				))}
				{!(byType.get("TXT") ?? []).length && <Empty />}
			</Panel>

			<Panel title={`Certificates (CT) — ${data?.certs.length ?? 0}`} className="mt-3.5">
				{data?.certs.length ? (
					data.certs.map((ct, i) => (
						<div key={`${ct.common_name}:${i}`} className="text-xs mb-1 flex items-center gap-2 flex-wrap">
							<span className="font-mono text-content break-all">{ct.common_name ?? "—"}</span>
							<span className="text-muted">{ct.issuer ?? ""}</span>
							<span className="text-muted">{fmt(ct.not_before)} → {fmt(ct.not_after)}</span>
						</div>
					))
				) : (
					<div className="text-xs text-muted">No CT records — run <em>Resolve now</em> or Watch to scan crt.sh.</div>
				)}
			</Panel>
		</Page>
	);
}

function Panel({
	title,
	action,
	children,
	className,
}: { title?: string; action?: ReactNode; children: ReactNode; className?: string }) {
	return (
		<Card className={className}>
			<CardBody>
				{(title || action) && (
					<div className="flex items-center justify-between mb-2">
						{title && <h2 className="text-sm font-semibold text-content">{title}</h2>}
						{action}
					</div>
				)}
				{children}
			</CardBody>
		</Card>
	);
}

function KV({ k, v }: { k: string; v?: string | null }) {
	return (
		<div>
			<div className="text-[11px] text-muted">{k}</div>
			<div className="text-sm text-content">{v ?? "—"}</div>
		</div>
	);
}
function Empty() {
	return <div className="text-xs text-muted">—</div>;
}
function fmt(v?: string | null): string {
	return v ? new Date(v).toLocaleDateString() : "—";
}
/** Date + time — used in the timeline so change-detection moments show when, not just the day. */
function fmtDateTime(v?: string | null): string {
	return v
		? new Date(v).toLocaleString(undefined, {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			})
		: "—";
}
